<script setup>
import { ref, computed, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useCentertelTags } from 'dashboard/composables/useCentertelTags';

const {
  activeLabels,
  availableLabels,
  canManageLabels,
  addTagWithValidation,
  removeTagWithValidation,
  currentChat,
  isLoadingConfigs,
} = useCentertelTags();

const searchQuery = ref('');
const showDropdown = ref(false);
const searchInput = ref(null);

// Filtro do autocomplete
const filteredLabels = computed(() => {
  const query = searchQuery.value.toLowerCase().trim();
  if (!query) return availableLabels.value.slice(0, 12);
  return availableLabels.value
    .filter(l => (l.description || l.title).toLowerCase().includes(query))
    .slice(0, 12);
});

// Fechar dropdown ao trocar de conversa
watch(
  () => currentChat.value?.id,
  () => {
    showDropdown.value = false;
    searchQuery.value = '';
  }
);

const onFocus = () => {
  if (canManageLabels.value) showDropdown.value = true;
};

const onBlur = () => {
  setTimeout(() => {
    showDropdown.value = false;
  }, 200);
};

const onAddLabel = async label => {
  searchQuery.value = '';
  showDropdown.value = false;

  const result = await addTagWithValidation(label);
  if (!result.ok) {
    useAlert(result.mensagem || 'Tag bloqueada');
    return;
  }
  if (result.aviso) {
    useAlert(result.aviso);
  }
};

const onRemoveLabel = async labelTitle => {
  const result = await removeTagWithValidation(labelTitle);
  if (!result.ok) {
    useAlert(result.mensagem || 'Não é possível remover esta tag');
  }
};

const onKeydown = e => {
  if (e.key === 'Enter' && searchQuery.value.trim()) {
    const match = filteredLabels.value[0];
    if (match) onAddLabel(match);
  }
  if (e.key === 'Escape') {
    showDropdown.value = false;
    searchInput.value?.blur();
  }
};
</script>

<template>
  <div
    v-if="currentChat?.id"
    class="flex items-center gap-1.5 px-3 py-1.5 border-b border-n-weak flex-wrap min-h-[36px]"
  >
    <!-- Tags ativas -->
    <span
      v-for="label in activeLabels"
      :key="label.id"
      class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white max-w-[160px]"
      :style="{ backgroundColor: label.color || '#6B7280' }"
    >
      <span class="truncate">{{ label.description || label.title }}</span>
      <button
        v-if="canManageLabels"
        class="flex items-center justify-center w-3.5 h-3.5 rounded-full hover:bg-white/30 transition-colors flex-shrink-0"
        @click.stop="onRemoveLabel(label._originalTitle || label.title)"
      >
        <span class="text-[10px] leading-none">✕</span>
      </button>
    </span>

    <!-- Input autocomplete -->
    <div v-if="canManageLabels" class="relative">
      <input
        ref="searchInput"
        v-model="searchQuery"
        type="text"
        :placeholder="activeLabels.length ? '+' : '+ etiqueta...'"
        class="bg-transparent border border-n-weak rounded-full px-2.5 py-0.5 text-xs outline-none w-[120px] focus:w-[160px] focus:border-n-strong transition-all text-n-slate-12 placeholder:text-n-slate-8"
        autocomplete="off"
        @focus="onFocus"
        @blur="onBlur"
        @keydown="onKeydown"
      />

      <!-- Dropdown -->
      <div
        v-if="showDropdown && filteredLabels.length > 0"
        class="absolute top-full left-0 mt-1 bg-n-background border border-n-weak rounded-lg shadow-lg z-50 min-w-[200px] max-h-[240px] overflow-y-auto"
      >
        <button
          v-for="label in filteredLabels"
          :key="label.id"
          class="flex items-center gap-2 w-full px-3 py-2 text-left text-xs hover:bg-n-alpha-1 transition-colors"
          @mousedown.prevent="onAddLabel(label)"
        >
          <span
            class="w-2.5 h-2.5 rounded-full flex-shrink-0"
            :style="{ backgroundColor: label.color || '#6B7280' }"
          />
          <span class="truncate text-n-slate-12">{{ label.description || label.title }}</span>
        </button>
      </div>
    </div>

    <!-- Loading configs -->
    <span
      v-if="isLoadingConfigs"
      class="text-[10px] text-n-slate-8"
    >
      ...
    </span>
  </div>
</template>
